import React, { useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import { useSelector } from "react-redux";
import { getProjContractorOptions } from "../../_helpers/contractorOptions";
import { getEquipOption } from "./utils/adrUtilsV3";
import "./styles/adrTentativeAgreement.css";
import AlertBox from "../_common/AlertBox";

/**
 * Container for TaEquipmentSetRows
 * @param {Object} props
 */
export function AdrTentativeAgreementModal_EquipmentSetV3(props) {
  const {
    tabId,
    projectId,
    status,
    isLocked,
    equipmentClient,
    updateEquipment,
    selectedContractors,
    changeJustificationModal,
  } = props;

  const equipmentSetOrder = useSelector((state) =>
    state.adr.openAdrs[tabId] ? state.adr.openAdrs[tabId].equipmentSetOrder || [] : null
  );
  const equipmentSetClient = useSelector((state) =>
    state.adr.openAdrs[tabId]
      ? state.adr.openAdrs[tabId].equipmentSetClient || null
      : null
  );
  const projContractorList = useSelector(
    (state) => state.contractors.projContractorLists[projectId] || null
  );
  const projEquipList = useSelector(
    (state) => state.equipment.projEquipLists[projectId] || null
  );

  const contractorOptions = useMemo(
    () =>
      projContractorList
        ? getProjContractorOptions(
            projContractorList.filter((c) => selectedContractors.includes(c.contractorId))
          )
        : null,
    [projContractorList, selectedContractors]
  );

  const equipmentOptions = useMemo(
    () =>
      projEquipList
        ? projEquipList
            .filter((e) => selectedContractors.includes(e.contractorId))
            .map((e) => ({
              ...getEquipOption(e),
              contractorId: e.contractorId,
            }))
        : null,
    [projEquipList, selectedContractors]
  );

  const validEquipmentSet = equipmentSetOrder
    ? equipmentSetOrder.filter((esrKey) => {
        const equipmentId = equipmentSetClient[esrKey].equipmentId;
        return (
          equipmentId in equipmentClient &&
          equipmentClient[equipmentId].contractorId &&
          equipmentClient[equipmentId].equipmentId &&
          selectedContractors.includes(equipmentClient[equipmentId].contractorId)
        );
      })
    : [];

  const renderHeader = () => {
    return (
      <div className="d-flex flex-row flex-wrap ta-wp__tr">
        <div className="ta-wp__th ta-wp__th--contractor">
          <span>Contractor</span>
        </div>
        <div className="ta-wp__th ta-wp__th--equip">
          <span>Number/Make/Desc.</span>
        </div>
        <div className="ta-wp__th ta-wp__th--hours">
          <span className="badge badge-info px-1 my-auto">RT</span>
        </div>
        <div className="ta-wp__th ta-wp__th--hours">
          <span className="badge badge-warning px-1 my-auto">OT</span>
        </div>
        <div className="ta-wp__td ta-wp__td--select">
          <span className="my-auto">
            <small>
              <b>TA Work?</b>
            </small>
          </span>
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className="w-100 p-1">
        <AlertBox icon="info-circle" color="info">
          To update your daily report with these hours, check "Apply TA hours to the daily
          report" below.
        </AlertBox>
      </div>
      {renderHeader()}
      <div className="d-flex flex-column w-100 ta-wp">
        {validEquipmentSet.length > 0 ? (
          validEquipmentSet.map((rowKey) => {
            const equipmentId = equipmentSetClient[rowKey].equipmentId;
            return (
              <AdrTentativeAgreementModal_EquipmentSetRowV3
                key={equipmentId}
                rowKey={equipmentId}
                status={status}
                isLocked={isLocked}
                taEquipment={equipmentClient[equipmentId]}
                contractorOptions={contractorOptions}
                equipmentOptions={equipmentOptions}
                updateEquipment={updateEquipment}
                changeJustificationModal={changeJustificationModal}
              />
            );
          })
        ) : (
          <div className="ta-wp__tr">
            <div className="ta-wp__td">
              <input
                name="noEquipmentToShow"
                id="noEquipmentToShow"
                className="form-control"
                value={"There are no equipment selected for this TA."}
                onChange={() => {}}
                disabled={true}
              />
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
}

function AdrTentativeAgreementModal_EquipmentSetRowV3(props) {
  const {
    rowKey,
    status,
    isLocked,
    contractorOptions,
    equipmentOptions,
    taEquipment,
    updateEquipment,
    changeJustificationModal,
  } = props;

  const [showChangeJustificationModal, _toggleChangeJustificationModal] = useState(false);
  const toggleChangeJustificationModal = () =>
    _toggleChangeJustificationModal(!showChangeJustificationModal);

  const changeAnnotation = useRef("");
  const cachedValues = useRef({
    regHours: taEquipment.regHours,
    otHours: taEquipment.otHours,
    excludeFromTa: taEquipment.excludeFromTa,
  });

  const selectedContractor = useMemo(
    () => contractorOptions.find((c) => c.value === taEquipment.contractorId) || null,
    [taEquipment.contractorId]
  );
  const selectedEquipment = useMemo(
    () => equipmentOptions.find((e) => e.value === taEquipment.equipmentId) || null,
    [taEquipment.equipmentId]
  );

  const updateCachedValues = () => {
    cachedValues.current = {
      regHours: taEquipment.regHours,
      otHours: taEquipment.otHours,
      excludeFromTa: taEquipment.excludeFromTa,
    };
  };

  const cancelChange = () => {
    updateEquipment(rowKey, {
      regHours: cachedValues.current.regHours,
      otHours: cachedValues.current.otHours,
      excludeFromTa: cachedValues.current.excludeFromTa,
    });
  };

  const handleChange_excludeFromTa = (e) => {
    updateEquipment(rowKey, {
      excludeFromTa: !e.target.checked,
    });
    if (isLocked) {
      const actionLabel = e.target.checked ? "Added" : "Removed";
      changeAnnotation.current = `${actionLabel} ${
        selectedEquipment.label
      }, (with reg. hours of ${+taEquipment.regHours || 0} and OT hours of ${
        +taEquipment.otHours || 0
      }) from this TA.`;
      toggleChangeJustificationModal();
    }
  };

  const handleChange_equipmentHours = (field) => (e) =>
    updateEquipment(rowKey, { [field]: e.target.value });

  const handleBlur_equipmentHours = (field) => (e) => {
    if (isLocked && (+taEquipment[field] || 0) !== (+cachedValues.current[field] || 0)) {
      const fieldLabel =
        field === "regHours" ? "reg. hours" : field === "otHours" ? "OT Hours" : "";
      changeAnnotation.current = `Changed ${fieldLabel} for ${
        selectedEquipment.label
      } from ${+cachedValues.current[field] || "0"} to ${e.target.value}.`;

      toggleChangeJustificationModal();
    }
  };

  return (
    <React.Fragment>
      <div className="d-flex flex-row flex-wrap ta-wp__tr">
        <div className="ta-wp__td ta-wp__td--contractor">
          <input
            name="contractorId"
            id={`equipContractorSelectId_${rowKey}`}
            className="form-control"
            value={selectedContractor.label}
            onChange={() => {}}
            disabled={true}
          />
        </div>
        <div className="ta-wp__td ta-wp__td--equip">
          <input
            name="equipmentId"
            id={`equipSelectId_${rowKey}`}
            value={selectedEquipment.label}
            className="form-control"
            onChange={() => {}}
            disabled={true}
          />
        </div>
        <div className="ta-wp__td ta-wp__td--hours">
          <input
            className="form-control px-1"
            type="number"
            id={`equipRegHoursId_${rowKey}`}
            name={`equipRegHours_${rowKey}`}
            value={taEquipment.regHours || ""}
            onChange={handleChange_equipmentHours("regHours")}
            onBlur={handleBlur_equipmentHours("regHours")}
            autoComplete="off"
            disabled={taEquipment.excludeFromTa || status.isReadOnly}
          />
        </div>
        <div className="ta-wp__td ta-wp__td--hours">
          <input
            className="form-control px-1"
            type="number"
            id={`equipOtHoursId_${rowKey}`}
            name={`equipOtHours_${rowKey}`}
            value={taEquipment.otHours || ""}
            onChange={handleChange_equipmentHours("otHours")}
            onBlur={handleBlur_equipmentHours("otHours")}
            autoComplete="off"
            disabled={taEquipment.excludeFromTa || status.isReadOnly}
          />
        </div>
        <div className="ta-wp__td ta-wp__td--select p-1">
          <input
            type="checkbox"
            name="includeInTa"
            className="align-middle py-auto"
            id={`taEquipmentSelect_${rowKey}`}
            style={{ width: "1.6rem", height: "1.6rem" }}
            value={!taEquipment.excludeFromTa}
            checked={!taEquipment.excludeFromTa}
            onTouchEnd={handleChange_excludeFromTa}
            onChange={handleChange_excludeFromTa}
            disabled={status.isReadOnly}
          />
        </div>
      </div>
      {showChangeJustificationModal &&
        changeJustificationModal({
          rowKey,
          showModal: showChangeJustificationModal,
          toggleModal: toggleChangeJustificationModal,
          changeAnnotation: changeAnnotation.current,
          cancelChange,
          updateCachedValues,
        })}
    </React.Fragment>
  );
}
